import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const LIBRARY_PATHS = ['/topics', '/highlights', '/feeds', '/sources', '/newsletters'];

const DashboardHeader = ({
  user,
  logout,
  userMenuOpen,
  setUserMenuOpen,
  userMenuRef,
}) => {
  const location = useLocation();
  const [libraryOpen, setLibraryOpen] = useState(false);
  const libraryRef = useRef(null);
  const libraryActive = LIBRARY_PATHS.some((p) => location.pathname.startsWith(p));

  useEffect(() => {
    if (!libraryOpen) return;
    const onClickOutside = (e) => {
      if (libraryRef.current && !libraryRef.current.contains(e.target)) {
        setLibraryOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [libraryOpen]);

  useEffect(() => { setLibraryOpen(false); }, [location.pathname]);

  return (
  <header className="bg-surface shadow-clay border-b-3 border-clay-border">
    <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
      <div className="flex justify-between items-center py-3 sm:py-4">
        {/* Logo and Nav */}
        <div className="flex items-center space-x-2 sm:space-x-8 min-w-0">
          {/* Logo with float animation */}
          <Link to="/dashboard" className="flex items-center flex-shrink-0 group">
            <div className="h-10 w-10 sm:h-12 sm:w-12 mr-2 sm:mr-3 transition-transform duration-300 group-hover:animate-bounce-gentle">
              <img src={new URL('../assets/logo.png', import.meta.url).href} alt="Read It Never" className="h-10 w-10 sm:h-12 sm:w-12 rounded-clay shadow-clay-sm" />
            </div>
            <div className="hidden sm:block">
              <h1 className="font-display text-xl font-bold text-heading">Read It Never</h1>
              <p className="text-sm text-secondary-text hidden md:block">Reading is slow. Just vibe it.</p>
            </div>
          </Link>

          {/* Navigation Links */}
          <nav className="flex items-center flex-wrap gap-1 sm:gap-1.5 lg:gap-3">
            <Link
              to="/daily"
              className="flex items-center px-1.5 py-1 sm:px-2.5 sm:py-2 lg:px-3 text-xs sm:text-sm font-semibold text-secondary-text hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/30 rounded-clay transition-all duration-200"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5 lg:mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              <span className="hidden lg:inline">Daily</span>
            </Link>
            <Link
              to="/briefing"
              className="flex items-center px-1.5 py-1 sm:px-2.5 sm:py-2 lg:px-3 text-xs sm:text-sm font-semibold text-secondary-text hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-clay transition-all duration-200"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5 lg:mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
              </svg>
              <span className="hidden lg:inline">Briefing</span>
            </Link>
            {/* Library dropdown */}
            <div className="relative" ref={libraryRef}>
              <button
                type="button"
                onClick={() => setLibraryOpen(!libraryOpen)}
                aria-expanded={libraryOpen}
                aria-haspopup="menu"
                className={`flex items-center px-1.5 py-1 sm:px-2.5 sm:py-2 lg:px-3 text-xs sm:text-sm font-semibold rounded-clay transition-all duration-200 ${
                  libraryActive
                    ? 'text-secondary-600 bg-secondary-50 dark:bg-secondary-900/30'
                    : 'text-secondary-text hover:text-secondary-600 hover:bg-secondary-50 dark:hover:bg-secondary-900/30'
                }`}
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5 lg:mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                <span className="hidden lg:inline">Library</span>
                <svg className={`w-3 h-3 ml-1 hidden lg:inline transition-transform ${libraryOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {libraryOpen && (
                <div className="absolute left-0 mt-2 w-52 bg-surface rounded-clay border-2 border-clay-border shadow-clay-lg z-50" role="menu">
                  <Link to="/topics" role="menuitem" onClick={() => setLibraryOpen(false)} className="flex items-center px-4 py-2.5 text-sm text-body-text hover:bg-clay-bg transition-colors">
                    <svg className="w-4 h-4 mr-3 text-tertiary-text" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                    Topics
                  </Link>
                  <Link to="/highlights" role="menuitem" onClick={() => setLibraryOpen(false)} className="flex items-center px-4 py-2.5 text-sm text-body-text hover:bg-clay-bg transition-colors">
                    <svg className="w-4 h-4 mr-3 text-tertiary-text" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                    </svg>
                    Highlights
                  </Link>
                  <Link to="/newsletters" role="menuitem" onClick={() => setLibraryOpen(false)} className="flex items-center px-4 py-2.5 text-sm text-body-text hover:bg-clay-bg transition-colors">
                    <svg className="w-4 h-4 mr-3 text-tertiary-text" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Newsletters
                  </Link>
                  <div className="border-t border-theme-border-subtle my-1" />
                  <Link to="/feeds" role="menuitem" onClick={() => setLibraryOpen(false)} className="flex items-center px-4 py-2.5 text-sm text-body-text hover:bg-clay-bg transition-colors">
                    <svg className="w-4 h-4 mr-3 text-tertiary-text" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 5c7.18 0 13 5.82 13 13M6 11a7 7 0 017 7m-6 0a1 1 0 110-2 1 1 0 010 2z" />
                    </svg>
                    Feeds
                  </Link>
                  <Link to="/sources" role="menuitem" onClick={() => setLibraryOpen(false)} className="flex items-center px-4 py-2.5 text-sm text-body-text hover:bg-clay-bg transition-colors">
                    <svg className="w-4 h-4 mr-3 text-tertiary-text" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Sources
                  </Link>
                </div>
              )}
            </div>
          </nav>
        </div>

        {/* User Info - inline on desktop, dropdown on mobile */}
        <div className="hidden sm:flex items-center space-x-4 flex-shrink-0">
          <span className="badge-fun-primary text-xs truncate" title={user?.email}>
            {user?.email?.split('@')[0]}
          </span>
          <Link
            to="/settings"
            className="p-1.5 text-tertiary-text hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-clay transition-all duration-200"
            title="Settings"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </Link>
          <button
            onClick={logout}
            className="btn-clay-ghost text-sm whitespace-nowrap"
          >
            Sign out
          </button>
        </div>

        {/* Mobile user menu */}
        <div className="relative sm:hidden" ref={userMenuRef}>
          <button
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            className="flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 text-sm font-bold hover:bg-primary-200 dark:hover:bg-primary-800/40 transition-colors"
            title={user?.email}
          >
            {user?.email?.charAt(0).toUpperCase()}
          </button>
          {userMenuOpen && (
            <div className="absolute right-0 mt-2 w-48 bg-surface rounded-clay border-2 border-clay-border shadow-clay-lg z-50">
              <div className="px-4 py-3 border-b border-theme-border-subtle">
                <p className="text-sm font-medium text-heading truncate">{user?.email}</p>
              </div>
              <Link
                to="/settings"
                className="flex items-center px-4 py-2.5 text-sm text-body-text hover:bg-clay-bg transition-colors"
                onClick={() => setUserMenuOpen(false)}
              >
                <svg className="w-4 h-4 mr-3 text-tertiary-text" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Settings
              </Link>
              <Link
                to="/stats"
                className="flex items-center px-4 py-2.5 text-sm text-body-text hover:bg-clay-bg transition-colors"
                onClick={() => setUserMenuOpen(false)}
              >
                <svg className="w-4 h-4 mr-3 text-tertiary-text" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                Stats
              </Link>
              <button
                onClick={() => { setUserMenuOpen(false); logout(); }}
                className="flex items-center w-full px-4 py-2.5 text-sm text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/30 transition-colors"
              >
                <svg className="w-4 h-4 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  </header>
  );
};

export default DashboardHeader;
